// --- Boards (Kanban) page ---
//
// Column-based issue/task view over configured boards (GitLab/GitHub/local).
// Subheader owns board picker + search + filters; body holds the kanban
// columns + issue modal + agent activity drawer. Client JS drives sortable
// drag-drop, live updates, and optimistic writes.
//
// Deduplicated from the old board-dashboard.ts monolith:
//   ui/tokens.ts         — :root, theme overrides, font/color vars
//   ui/components.css.ts — .ax-badge / .ax-card / .ax-field reusable bits
//   topbar.ts            — .ax-topbar / .ax-subheader / .ax-theme-switch
//   HERE                 — subheader inputs + kanban columns + issue modal +
//                          activity drawer + client JS
//
// The page accepts renderBoardPage({ peers }) — daemon-config awareness
// stays in board-dashboard.ts where the routing happens.

import { renderShell, type TopbarPeer } from ".."

export interface BoardsPageOpts {
  peers?: TopbarPeer[]
  currentPeerId?: string
}

export function renderBoardsPage(opts: BoardsPageOpts = {}): string {
  const subheader = `
    <select id="boardPicker" title="Switch board"></select>
    <div class="search">
      <span class="icon">⌕</span>
      <input id="filterSearch" type="search" placeholder="Search or filter…" autocomplete="off" />
    </div>
    <select id="filterAssignee" title="Assignee"><option value="">All assignees</option></select>
    <select id="filterMilestone" title="Milestone"><option value="">Any milestone</option></select>
    <div id="filterLabels" class="chips"></div>
    <div class="spacer"></div>
    <button id="activityBtn" title="Agent activity (a)">◉ Activity</button>
    <button id="newIssueBtn" title="New issue">+ New</button>
    <button id="refreshBtn" title="Refresh (r)">↻</button>`

  const body = `<aside id="activity-panel" class="activity-panel hidden" aria-hidden="true">
  <header>
    <h2>Agent Activity</h2>
    <span id="activity-source" class="source"></span>
    <button id="activity-close" class="x" title="Close">✕</button>
  </header>
  <div id="activity-body">Loading…</div>
  <footer id="activity-events" class="events"></footer>
</aside>
<main id="columns"></main>
<div id="toast"></div>
<div id="modal" class="modal" aria-hidden="true">
  <div class="modal-backdrop"></div>
  <div class="modal-panel" role="dialog" aria-modal="true">
    <header class="modal-h">
      <div class="modal-title-row">
        <span id="m-iid" class="iid"></span>
        <input id="m-title" type="text" placeholder="Issue title" />
      </div>
      <button id="m-close" class="x" title="Close (esc)">✕</button>
    </header>
    <div class="modal-body">
      <div class="m-main">
        <div id="m-draft" class="draft-box" hidden>
          <label class="fld">
            <span>✨ Describe the issue (the agent will draft a proper title + description)</span>
            <textarea id="m-rough" rows="3" placeholder="e.g. when I upload a CSV over 500 rows in Receiving Records the import silently fails"></textarea>
          </label>
          <div class="draft-row">
            <select id="m-draft-agent" title="Drafting agent"><option value="">Agent…</option></select>
            <button id="m-draft-go" class="btn-primary" type="button">✨ Draft</button>
            <span id="m-draft-hint" class="hint"></span>
          </div>
        </div>
        <div class="fld">
          <div class="desc-head">
            <span>Description</span>
            <div class="desc-tabs" role="tablist">
              <button type="button" class="desc-tab" data-tab="preview" role="tab">Preview</button>
              <button type="button" class="desc-tab active" data-tab="write" role="tab">Write</button>
            </div>
          </div>
          <div id="m-desc-preview" class="desc-preview" hidden></div>
          <textarea id="m-desc" rows="10" placeholder="Markdown supported (GitLab flavored)"></textarea>
        </div>
        <div class="fld">
          <span>Labels</span>
          <div id="m-labels" class="chip-picker"></div>
        </div>
      </div>
      <aside class="m-side">
        <div class="fld">
          <span>State</span>
          <div id="m-state" class="state-toggle">
            <button data-state="opened">Open</button>
            <button data-state="closed">Closed</button>
          </div>
        </div>
        <div class="fld">
          <span>Assignees</span>
          <div id="m-assignees" class="chip-picker"></div>
        </div>
        <div class="fld">
          <span>Milestone</span>
          <select id="m-milestone"><option value="">None</option></select>
        </div>
        <div class="fld muted">
          <span>Author</span>
          <div id="m-author"></div>
        </div>
        <div class="fld muted">
          <span>Created</span>
          <div id="m-created"></div>
        </div>
        <div class="fld muted">
          <span>Updated</span>
          <div id="m-updated"></div>
        </div>
        <a id="m-link" class="ext-link" href="#" target="_blank" rel="noopener">Open in GitLab ↗</a>
      </aside>
    </div>
    <footer class="modal-f">
      <div id="m-hint" class="hint"></div>
      <div class="spacer"></div>
      <button id="m-cancel" class="btn-ghost">Cancel</button>
      <button id="m-save" class="btn-primary">Save</button>
    </footer>
  </div>
</div>
<script src="/sortable.min.js"></script>`

  return renderShell({
    title: "AgentX · Boards",
    activeTab: "boards",
    subtitle: "Boards",
    subheader,
    peers: opts.peers,
    currentPeerId: opts.currentPeerId,
    noMain: true,
    body,
    css: BOARDS_PAGE_CSS,
    scripts: `<script>${BOARDS_PAGE_SCRIPT}</script>`,
    headExtras: `<script>
      // The boards page has its own connection dot + wants body height:100%
      // for the full-viewport kanban layout. Inject the dot into the topbar
      // and override body height at runtime so the shared shell doesn't
      // need a dedicated opt-in.
      document.addEventListener('DOMContentLoaded', () => {
        document.documentElement.style.height = '100%';
        document.body.style.height = '100%';
        const right = document.querySelector('.ax-topbar__right');
        if (right && !right.querySelector('.conn')) {
          const dot = document.createElement('div');
          dot.id = 'conn'; dot.className = 'conn ok'; dot.title = 'ready';
          dot.textContent = '●';
          right.insertBefore(dot, right.firstChild);
        }
      });
    </script>`,
  })
}

const BOARDS_PAGE_CSS = `
/* Kanban subheader — replaces the legacy toolbar that used to live in <header>. */
.ax-subheader select, .ax-subheader input, .ax-subheader button {
  background: var(--col); color: var(--text); border: 1px solid var(--border);
  padding: 6px 10px; border-radius: 6px; font: inherit; outline: none; }
.ax-subheader select:focus, .ax-subheader input:focus { border-color: var(--accent); }
.ax-subheader .search { position: relative; }
.ax-subheader .search .icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--muted); pointer-events: none; font-size: 14px; }
.ax-subheader .search input { padding-left: 28px; width: 260px; }
.ax-subheader button { cursor: pointer; }
.ax-subheader button:hover { border-color: var(--accent); }
.ax-subheader .chips { display: flex; gap: 4px; flex-wrap: wrap; }
.ax-subheader #newIssueBtn { background: var(--accent); color: #fff; border-color: var(--accent); }
.ax-subheader #newIssueBtn:hover { filter: brightness(1.1); }
.ax-topbar__right .conn { font-size: 14px; line-height: 1; }
.ax-topbar__right .conn.ok { color: var(--green); }
.ax-topbar__right .conn.warn { color: var(--yellow); }
.ax-topbar__right .conn.err { color: var(--red); }

main#columns { display: flex; gap: 10px; padding: 12px; overflow-x: auto; align-items: stretch;
  min-height: calc(100vh - 60px); }
.col { background: var(--col); border: 1px solid var(--border); border-radius: 8px;
  min-width: 280px; max-width: 320px; flex: 1 0 280px; display: flex; flex-direction: column;
  max-height: calc(100vh - 84px); }
.col .accent { height: 3px; border-top-left-radius: 7px; border-top-right-radius: 7px; background: var(--gray); }
.col header.col-h { margin: 0; padding: 10px 12px; background: var(--col-hdr);
  border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 8px;
  position: sticky; top: 0; z-index: 1; border-radius: 0; }
.col header.col-h h3 { margin: 0; font-size: 13px; font-weight: 600; color: var(--text); flex: 1; }
.col header.col-h .count { background: rgba(255,255,255,0.08); color: var(--muted);
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500; }
.stack { padding: 8px; display: flex; flex-direction: column; gap: 8px; overflow-y: auto;
  flex: 1; min-height: 40px; }

.card { background: var(--card); border: 1px solid var(--border); border-radius: 6px;
  padding: 10px 12px; cursor: grab; transition: border-color .15s, transform .15s;
  display: flex; flex-direction: column; gap: 6px; }
.card:hover { border-color: var(--accent); }
.card.closed { opacity: 0.75; }
.card .title { font-weight: 500; font-size: 13px; line-height: 1.4; color: var(--text);
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; word-break: break-word; }
.card .labels { display: flex; gap: 4px; flex-wrap: wrap; }
.card .label { font-size: 10px; padding: 2px 8px; border-radius: 10px; font-weight: 500;
  background: rgba(99, 102, 241, 0.25); color: #e6e8ef; white-space: nowrap; line-height: 1.4; }
.card .foot { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--muted);
  flex-wrap: wrap; }
.card .foot .iid { font-family: ui-monospace, monospace; }
.card .foot .milestone { background: rgba(99,102,241,0.15); color: var(--accent);
  padding: 1px 6px; border-radius: 3px; font-size: 10px; }
.card .foot .est { background: rgba(255,255,255,0.08); padding: 1px 6px; border-radius: 3px; font-size: 10px; }
.card .foot .date { margin-left: auto; }
.card .foot a.link { color: var(--muted); text-decoration: none; }
.card .foot a.link:hover { color: var(--accent); }

.avatar { display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 600;
  color: white; flex-shrink: 0; }
.avatar.unassigned { background: var(--gray); }

.sortable-ghost { opacity: 0.4; }
.sortable-drag { cursor: grabbing; }
.sortable-chosen { border-color: var(--accent); }
.empty { color: var(--muted); font-style: italic; text-align: center; padding: 16px; font-size: 12px; }

#toast { position: fixed; right: 16px; bottom: 16px; padding: 10px 14px;
  background: var(--red); color: white; border-radius: 6px; font-size: 13px;
  max-width: 360px; display: none; z-index: 200; box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
#toast.show { display: block; }

/* Multi-assignee stack on cards */
.avatar-stack { display: inline-flex; }
.avatar-stack .avatar { margin-left: -6px; border: 2px solid var(--card); }
.avatar-stack .avatar:first-child { margin-left: 0; }
.avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }

/* Column add button */
.col header.col-h .add { background: transparent; color: var(--muted); border: 1px solid var(--border);
  padding: 0 8px; line-height: 22px; border-radius: 4px; cursor: pointer; font-size: 14px; }
.col header.col-h .add:hover { color: var(--accent); border-color: var(--accent); }

/* --- Modal --- */
.modal { position: fixed; inset: 0; z-index: 150; display: none; }
.modal.open { display: block; }
.modal-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(2px); }
.modal-panel { position: relative; max-width: 940px; width: calc(100% - 40px); height: calc(100vh - 60px);
  margin: 30px auto; background: var(--card); border: 1px solid var(--border); border-radius: 10px;
  display: flex; flex-direction: column; box-shadow: 0 16px 48px rgba(0,0,0,0.55); overflow: hidden; }
.modal-h { display: flex; gap: 10px; padding: 12px 16px; border-bottom: 1px solid var(--border); align-items: center; }
.modal-h .modal-title-row { flex: 1; display: flex; gap: 10px; align-items: center; }
.modal-h .iid { font-family: ui-monospace, monospace; color: var(--muted); font-size: 13px; }
.modal-h input[type="text"] { flex: 1; background: var(--col); border: 1px solid var(--border); color: var(--text);
  padding: 8px 10px; border-radius: 6px; font-size: 15px; font-weight: 500; outline: none; }
.modal-h input[type="text"]:focus { border-color: var(--accent); }
.modal-h .x { background: transparent; color: var(--muted); border: none; font-size: 18px; cursor: pointer; padding: 6px 8px; }
.modal-h .x:hover { color: var(--text); }

.modal-body { flex: 1; display: grid; grid-template-columns: 1fr 300px; gap: 0;
  overflow: hidden; min-height: 0; }
.modal-body .m-main { padding: 16px; overflow-y: auto; min-height: 0; }
.modal-body .m-side { padding: 16px; border-left: 1px solid var(--border); background: var(--col);
  overflow-y: auto; min-height: 0; display: flex; flex-direction: column; gap: 14px; }

.fld { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.fld > span { font-size: 11px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; }
.fld.muted > div { font-size: 12px; color: var(--muted); }
.fld textarea { background: var(--col); border: 1px solid var(--border); color: var(--text);
  padding: 10px; border-radius: 6px; font: 13px/1.5 ui-monospace, "SF Mono", monospace; outline: none; resize: vertical; }
.fld textarea:focus { border-color: var(--accent); }
.fld select { background: var(--col); border: 1px solid var(--border); color: var(--text);
  padding: 6px 10px; border-radius: 6px; font: inherit; outline: none; }

/* Description edit/preview tabs */
.desc-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.desc-tabs { display: inline-flex; background: var(--col); border: 1px solid var(--border); border-radius: 6px; overflow: hidden; }
.desc-tabs button { background: transparent; border: none; color: var(--muted); padding: 4px 10px; cursor: pointer; font: inherit; font-size: 11px; }
.desc-tabs button.active { background: var(--accent); color: white; }
.desc-preview { background: var(--col); border: 1px solid var(--border); border-radius: 6px;
  padding: 12px 14px; min-height: 120px; max-height: 420px; overflow-y: auto; line-height: 1.55; }
.desc-preview.empty { color: var(--muted); font-style: italic; }
.desc-preview h1 { font-size: 18px; margin: 16px 0 8px; border-bottom: 1px solid var(--border); padding-bottom: 4px; }
.desc-preview h2 { font-size: 16px; margin: 14px 0 6px; }
.desc-preview h3 { font-size: 14px; margin: 12px 0 4px; color: var(--text); }
.desc-preview p { margin: 6px 0; }
.desc-preview ul, .desc-preview ol { margin: 6px 0; padding-left: 20px; }
.desc-preview li { margin: 2px 0; }
.desc-preview li.task { list-style: none; margin-left: -16px; }
.desc-preview li.task input { margin-right: 6px; vertical-align: middle; }
.desc-preview code { background: rgba(255,255,255,0.08); padding: 1px 5px; border-radius: 3px;
  font: 12px ui-monospace, "SF Mono", monospace; color: #e6e8ef; }
.desc-preview pre { background: #0a0c11; border: 1px solid var(--border); border-radius: 6px;
  padding: 10px; overflow-x: auto; margin: 8px 0; }
.desc-preview pre code { background: transparent; padding: 0; font-size: 12px; line-height: 1.45; color: #d8dce6; }
.desc-preview blockquote { border-left: 3px solid var(--accent); padding: 2px 12px; margin: 8px 0;
  color: var(--muted); background: rgba(99,102,241,0.08); border-radius: 0 4px 4px 0; }
.desc-preview a { color: var(--accent); text-decoration: none; }
.desc-preview a:hover { text-decoration: underline; }
.desc-preview hr { border: none; border-top: 1px solid var(--border); margin: 12px 0; }
.desc-preview table { border-collapse: collapse; margin: 8px 0; font-size: 12px; }
.desc-preview th, .desc-preview td { border: 1px solid var(--border); padding: 4px 8px; text-align: left; }
.desc-preview th { background: var(--col-hdr); font-weight: 600; }
.desc-preview img { max-width: 100%; border-radius: 4px; margin: 6px 0; }
.desc-preview del { color: var(--muted); }
.desc-preview .ref { background: rgba(99,102,241,0.15); padding: 0 4px; border-radius: 3px; font-family: ui-monospace, monospace; font-size: 12px; }

.chip-picker { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 6px;
  background: var(--col); border: 1px solid var(--border); border-radius: 6px; min-height: 36px; }
.chip-picker .chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px;
  border-radius: 10px; font-size: 11px; background: rgba(99,102,241,0.25); color: #e6e8ef; font-weight: 500; }
.chip-picker .chip .remove { cursor: pointer; color: rgba(255,255,255,0.7); margin-left: 2px; }
.chip-picker .chip .remove:hover { color: white; }
.chip-picker .add-chip { background: transparent; border: 1px dashed var(--border); color: var(--muted);
  padding: 3px 10px; border-radius: 10px; cursor: pointer; font: inherit; font-size: 11px; }
.chip-picker .add-chip:hover { border-color: var(--accent); color: var(--accent); }

.chip-picker .dropdown { position: relative; }
.chip-picker .dropdown .menu { position: absolute; top: 100%; left: 0; margin-top: 4px;
  background: var(--card); border: 1px solid var(--border); border-radius: 6px; min-width: 240px;
  max-height: 280px; overflow-y: auto; box-shadow: 0 8px 20px rgba(0,0,0,0.4); z-index: 10; padding: 4px; }
.chip-picker .dropdown .menu input.search { width: 100%; background: var(--col); border: 1px solid var(--border);
  color: var(--text); padding: 6px 10px; border-radius: 4px; font: inherit; margin-bottom: 4px; outline: none; }
.chip-picker .dropdown .menu .opt { padding: 6px 10px; cursor: pointer; border-radius: 4px;
  font-size: 12px; display: flex; align-items: center; gap: 8px; }
.chip-picker .dropdown .menu .opt:hover { background: var(--col); }
.chip-picker .dropdown .menu .opt.selected { background: rgba(99,102,241,0.15); color: var(--accent); }
.chip-picker .dropdown .menu .empty { padding: 10px; color: var(--muted); font-size: 12px; text-align: center; }

.state-toggle { display: inline-flex; background: var(--col); border: 1px solid var(--border); border-radius: 6px; overflow: hidden; }
.state-toggle button { background: transparent; border: none; color: var(--muted); padding: 6px 12px;
  cursor: pointer; font: inherit; font-size: 12px; }
.state-toggle button.active[data-state="opened"] { background: var(--green); color: white; }
.state-toggle button.active[data-state="closed"] { background: var(--gray); color: white; }

.ext-link { color: var(--muted); text-decoration: none; font-size: 12px; margin-top: auto; padding-top: 10px;
  border-top: 1px solid var(--border); }
.ext-link:hover { color: var(--accent); }

.modal-f { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--border); }
.modal-f .spacer { flex: 1; }
.modal-f .hint { color: var(--muted); font-size: 12px; }
.btn-primary { background: var(--accent); color: white; border: 1px solid var(--accent);
  padding: 7px 16px; border-radius: 6px; cursor: pointer; font: inherit; font-weight: 500; }
.btn-primary:hover { filter: brightness(1.1); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-ghost { background: transparent; color: var(--text); border: 1px solid var(--border);
  padding: 7px 14px; border-radius: 6px; cursor: pointer; font: inherit; }
.btn-ghost:hover { border-color: var(--accent); color: var(--accent); }

@media (max-width: 720px) {
  .modal-body { grid-template-columns: 1fr; }
  .modal-body .m-side { border-left: none; border-top: 1px solid var(--border); }
}

/* --- Activity panel --- */
.activity-panel { position: fixed; top: 0; right: 0; bottom: 0; width: 340px;
  background: var(--card); border-left: 1px solid var(--border); z-index: 140;
  display: flex; flex-direction: column; box-shadow: -8px 0 24px rgba(0,0,0,0.35);
  transition: transform 0.2s ease; }
.activity-panel.hidden { transform: translateX(100%); pointer-events: none; }
.activity-panel > header { display: flex; align-items: center; gap: 8px; padding: 12px 14px;
  border-bottom: 1px solid var(--border); background: var(--col-hdr); }
.activity-panel > header h2 { margin: 0; font-size: 13px; font-weight: 600; flex: 1; }
.activity-panel > header .source { font-size: 10px; color: var(--muted); font-family: ui-monospace, monospace; }
.activity-panel > header .x { background: transparent; border: none; color: var(--muted); font-size: 16px;
  cursor: pointer; padding: 4px 8px; }
.activity-panel > header .x:hover { color: var(--text); }
#activity-body { flex: 1; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.agent-card { background: var(--col); border: 1px solid var(--border); border-radius: 6px;
  padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; }
.agent-card .top { display: flex; align-items: center; gap: 8px; }
.agent-card .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--gray); flex-shrink: 0; }
.agent-card .dot.active { background: var(--green); box-shadow: 0 0 6px var(--green); animation: pulse 1.2s ease-in-out infinite; }
.agent-card .dot.errored { background: var(--red); }
.agent-card .name { font-weight: 600; font-size: 13px; flex: 1; }
.agent-card .tier { font-size: 10px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; }
.agent-card .meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.agent-card .model { font-family: ui-monospace, monospace; font-size: 10px; color: var(--accent);
  background: rgba(99,102,241,0.12); padding: 1px 7px; border-radius: 3px; letter-spacing: 0.2px; }
.agent-card .last { font-size: 10px; color: var(--muted); }
.agent-card .last.muted { font-style: italic; opacity: 0.7; }
.agent-card .stats { display: flex; gap: 10px; font-size: 11px; color: var(--muted); }
.agent-card .stats b { color: var(--text); font-weight: 600; }
.agent-card .stats .errors b { color: var(--red); }
.agent-card .current { font-size: 11px; color: var(--muted); padding-top: 4px; border-top: 1px dashed var(--border);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agent-card .current b { color: var(--text); font-weight: 500; }
@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }

.activity-panel .events { padding: 10px 14px; max-height: 200px; overflow-y: auto;
  border-top: 1px solid var(--border); background: #0a0c11; font: 11px/1.5 ui-monospace, monospace; }
.activity-panel .events .evt { color: var(--muted); margin-bottom: 4px; }
.activity-panel .events .evt .ts { color: #4a4e5a; }
.activity-panel .events .evt .who { color: var(--accent); }
.activity-panel .events .evt .what { color: var(--text); }
.activity-panel .events .evt.err .what { color: var(--red); }

/* Shift the main grid left when activity panel is open so cards aren't hidden */
body.panel-open main#columns { padding-right: 352px; }
body.panel-open header { padding-right: 356px; }

header #activityBtn.on { background: var(--accent); color: white; border-color: var(--accent); }
header a.link { color: var(--text); text-decoration: none; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 12px; }
header a.link:hover { color: var(--accent); border-color: var(--accent); }

/* AI draft row inside the create modal */
.draft-box { background: var(--col); border: 1px dashed var(--accent); border-radius: 8px;
  padding: 10px 12px; margin-bottom: 14px; }
.draft-box .fld { margin-bottom: 8px; }
.draft-box .draft-row { display: flex; gap: 8px; align-items: center; }
.draft-box .draft-row select { flex: 0 0 160px; }
.draft-box .draft-row .hint { color: var(--muted); font-size: 12px; flex: 1; }
.draft-box .draft-row .hint.err { color: var(--red); }
.draft-box .draft-row .hint.ok { color: var(--green); }
`
const BOARDS_PAGE_SCRIPT = `
'use strict';
const state = {
  boards: [], current: null, items: {},
  filter: { q: '', assignee: '', label: '', milestone: '' },
  // Per-board caches — members + milestones + known labels.
  meta: { members: [], milestones: [], labels: [] },
  modal: { mode: null, id: null, original: null, draft: null }
};

function csrfHeaders(extra) {
  return Object.assign({ 'Content-Type': 'application/json', 'X-Requested-With': 'agentx-board' }, extra || {});
}
function toast(msg, kind) {
  const el = document.getElementById('toast');
  el.textContent = msg;
  el.style.background = kind === 'ok' ? 'var(--green)' : 'var(--red)';
  el.classList.add('show');
  clearTimeout(toast._t);
  toast._t = setTimeout(() => el.classList.remove('show'), 4000);
}

async function loadBoards() {
  const r = await fetch('/api/boards');
  if (!r.ok) throw new Error('failed to load boards');
  state.boards = await r.json();
  const picker = document.getElementById('boardPicker');
  picker.innerHTML = '';
  for (const b of state.boards) {
    const opt = document.createElement('option');
    opt.value = b.id; opt.textContent = b.name || b.id;
    picker.appendChild(opt);
  }
  picker.onchange = () => switchBoard(picker.value);
  if (state.boards.length) switchBoard(state.boards[0].id);
}

async function switchBoard(id) {
  state.current = state.boards.find(b => b.id === id);
  state.meta = { members: [], milestones: [], labels: [] };
  renderColumns();
  populateFilters();
  await loadItems();
  // Fetch members + milestones in the background for pickers.
  loadMeta();
}

async function loadMeta() {
  if (!state.current) return;
  try {
    const [mem, ms] = await Promise.all([
      fetch(\`/api/boards/\${state.current.id}/members\`).then(r => r.ok ? r.json() : []),
      fetch(\`/api/boards/\${state.current.id}/milestones\`).then(r => r.ok ? r.json() : []),
    ]);
    state.meta.members = mem;
    state.meta.milestones = ms;
    state.meta.labels = collectKnownLabels();
  } catch (e) { /* non-fatal */ }
}

function collectKnownLabels() {
  const set = new Set();
  for (const list of Object.values(state.items)) for (const i of list) {
    for (const l of (i.labels || [])) set.add(l);
  }
  return [...set].sort();
}

function populateFilters() {
  if (!state.current) return;
  const labelsEl = document.getElementById('filterLabels');
  labelsEl.innerHTML = '';
  for (const lbl of state.current.labels || []) {
    const chip = document.createElement('span');
    chip.className = 'label';
    chip.style.cursor = 'pointer';
    chip.style.background = lbl.color || labelColor(lbl.name);
    chip.style.color = pickTextColor(lbl.color);
    chip.textContent = lbl.name;
    chip.onclick = () => {
      state.filter.label = state.filter.label === lbl.name ? '' : lbl.name;
      renderChipStates(); loadItems();
    };
    labelsEl.appendChild(chip);
  }
}
function renderChipStates() {
  document.querySelectorAll('#filterLabels .label').forEach(chip => {
    chip.style.opacity = (state.filter.label && chip.textContent !== state.filter.label) ? '0.35' : '1';
  });
}

function renderColumns() {
  const main = document.getElementById('columns');
  main.innerHTML = '';
  if (!state.current) return;
  for (const col of state.current.columns) {
    const section = document.createElement('section');
    section.className = 'col';
    section.dataset.colId = col.id;
    const canAdd = col.kind !== 'closed';
    section.innerHTML = \`
      <div class="accent" style="background:\${col.accent || 'var(--gray)'}"></div>
      <header class="col-h">
        <h3>\${escapeHtml(col.title)}</h3>
        <span class="count">0</span>
        \${canAdd ? \`<button class="add" data-col-id="\${col.id}" title="New issue in this column">+</button>\` : ''}
      </header>
      <div class="stack" data-col-id="\${col.id}"></div>
    \`;
    main.appendChild(section);
  }
  document.querySelectorAll('.col .add').forEach(btn => {
    btn.onclick = () => openCreateModal(btn.dataset.colId);
  });
  document.querySelectorAll('.stack').forEach(el => {
    Sortable.create(el, {
      group: 'board', animation: 150,
      onEnd: async (ev) => {
        const from = ev.from.dataset.colId;
        const to = ev.to.dataset.colId;
        if (from === to) return;
        const itemId = ev.item.dataset.id;
        try {
          const r = await fetch(\`/api/boards/\${state.current.id}/items/\${encodeURIComponent(itemId)}/move\`, {
            method: 'PATCH', headers: csrfHeaders(), body: JSON.stringify({ from, to })
          });
          if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText);
          toast('Moved ✓', 'ok');
          updateCounts();
        } catch (e) {
          ev.from.insertBefore(ev.item, ev.from.children[ev.oldIndex] || null);
          updateCounts();
          toast('Move failed: ' + (e.message || 'unknown'));
        }
      }
    });
  });
}

function updateCounts() {
  document.querySelectorAll('.col').forEach(col => {
    const n = col.querySelector('.stack').children.length;
    const empty = col.querySelector('.stack .empty');
    const real = empty ? 0 : n;
    col.querySelector('.count').textContent = real;
  });
}

async function loadItems() {
  const conn = document.getElementById('conn');
  conn.className = 'conn warn'; conn.title = 'loading…';
  if (!state.current) return;
  const params = new URLSearchParams();
  if (state.filter.q) params.set('q', state.filter.q);
  if (state.filter.assignee) params.set('assignee', state.filter.assignee);
  if (state.filter.label) params.set('label', state.filter.label);
  if (state.filter.milestone) params.set('milestone', state.filter.milestone);
  try {
    const r = await fetch(\`/api/boards/\${state.current.id}/items?\${params.toString()}\`);
    if (!r.ok) throw new Error(await r.text());
    const data = await r.json();
    state.items = data.columns;
    paintItems();
    populateAssigneeFilter();
    populateMilestoneFilter();
    conn.className = 'conn ok'; conn.title = 'ok';
  } catch (e) {
    conn.className = 'conn err'; conn.title = 'load failed';
    toast('Load failed: ' + (e.message || 'unknown'));
  }
}

function populateAssigneeFilter() {
  const set = new Set();
  for (const list of Object.values(state.items)) for (const i of list) if (i.assignee) set.add(i.assignee);
  const sel = document.getElementById('filterAssignee');
  const current = sel.value;
  sel.innerHTML = '<option value="">All assignees</option>';
  for (const a of [...set].sort()) {
    const o = document.createElement('option'); o.value = a; o.textContent = a;
    sel.appendChild(o);
  }
  sel.value = current;
}
function populateMilestoneFilter() {
  const set = new Set();
  for (const list of Object.values(state.items)) for (const i of list) if (i.milestone) set.add(i.milestone);
  const sel = document.getElementById('filterMilestone');
  const current = sel.value;
  sel.innerHTML = '<option value="">Any milestone</option>';
  for (const m of [...set].sort()) {
    const o = document.createElement('option'); o.value = m; o.textContent = m;
    sel.appendChild(o);
  }
  sel.value = current;
}

function paintItems() {
  if (!state.current) return;
  for (const col of state.current.columns) {
    const stack = document.querySelector(\`.stack[data-col-id="\${col.id}"]\`);
    if (!stack) continue;
    stack.innerHTML = '';
    const list = state.items[col.id] || [];
    if (list.length === 0) {
      const empty = document.createElement('div'); empty.className = 'empty'; empty.textContent = 'Empty';
      stack.appendChild(empty);
    } else {
      for (const item of list) stack.appendChild(renderCard(item, col));
    }
  }
  updateCounts();
}

function renderCard(item, col) {
  const card = document.createElement('div');
  card.className = 'card' + (item.state === 'closed' ? ' closed' : '');
  card.dataset.id = item.id;
  const iid = item.id.split(':').slice(-1)[0];

  // Filter out Status::* scoped labels from display (represented by the column).
  const details = (item.labelDetails || (item.labels || []).map(n => ({ name: n })))
    .filter(l => !/^Status::/i.test(l.name));
  const labelChips = details.slice(0, 5).map(l => {
    const bg = l.color || labelColor(l.name);
    const fg = l.text_color || pickTextColor(bg);
    return \`<span class="label" title="\${escapeHtml(l.name)}" style="background:\${bg};color:\${fg}">\${escapeHtml(l.name)}</span>\`;
  }).join('');

  const assignees = (item.assignees && item.assignees.length)
    ? item.assignees
    : (item.assignee ? [{ username: item.assignee }] : []);
  const avatarStack = assignees.length
    ? \`<span class="avatar-stack">\${assignees.slice(0, 3).map(renderAvatar).join('')}</span>\`
    : \`<span class="avatar unassigned" title="unassigned">?</span>\`;

  const milestone = item.milestone ? \`<span class="milestone" title="Milestone">🏁 \${escapeHtml(item.milestone)}</span>\` : '';
  const est = item.estimatedSeconds ? \`<span class="est" title="Estimate">⏱ \${fmtDuration(item.estimatedSeconds)}</span>\` : '';
  const date = item.updatedAt ? \`<span class="date" title="Updated \${escapeHtml(item.updatedAt)}">\${fmtDate(item.updatedAt)}</span>\` : '';
  const link = item.url ? \`<a class="link" href="\${escapeHtml(item.url)}" target="_blank" rel="noopener" title="Open in GitLab" onclick="event.stopPropagation()">↗</a>\` : '';

  card.innerHTML = \`
    <div class="title">\${escapeHtml(item.title || '(untitled)')}</div>
    \${labelChips ? \`<div class="labels">\${labelChips}</div>\` : ''}
    <div class="foot">
      \${avatarStack}
      <span class="iid">#\${escapeHtml(iid)}</span>
      \${milestone}
      \${est}
      \${date}
      \${link}
    </div>
  \`;
  card.addEventListener('click', (e) => {
    if (e.target.closest('a,button')) return;
    openDetailModal(item.id);
  });
  return card;
}

function renderAvatar(a) {
  const name = a.name || a.username || '?';
  const img = a.avatarUrl ? \`<img src="\${escapeHtml(a.avatarUrl)}" alt="">\` : '';
  const bg = labelColor(a.username || name);
  return \`<span class="avatar" title="\${escapeHtml(name)}" style="background:\${bg}">\${img || escapeHtml(initials(name))}</span>\`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function initials(s) {
  const parts = String(s).replace(/[_\\-\\.]+/g, ' ').trim().split(/\\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
function labelColor(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return 'hsl(' + (h % 360) + ', 55%, 45%)';
}
function pickTextColor(bg) {
  if (!bg) return '#fff';
  if (bg.startsWith('#') && bg.length >= 7) {
    const r = parseInt(bg.slice(1,3),16), g = parseInt(bg.slice(3,5),16), b = parseInt(bg.slice(5,7),16);
    return (r*299 + g*587 + b*114) / 1000 >= 160 ? '#111' : '#fff';
  }
  return '#fff';
}
function fmtDuration(sec) {
  if (!sec) return '';
  const m = Math.round(sec / 60);
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60); const rem = m % 60;
  return rem ? \`\${h}h\${rem}m\` : h + 'h';
}
function fmtDate(iso) {
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }

document.getElementById('filterSearch').oninput = debounce(e => { state.filter.q = e.target.value; loadItems(); }, 250);
document.getElementById('filterAssignee').onchange = e => { state.filter.assignee = e.target.value; loadItems(); };
document.getElementById('filterMilestone').onchange = e => { state.filter.milestone = e.target.value; loadItems(); };
document.getElementById('refreshBtn').onclick = () => loadItems();
document.getElementById('newIssueBtn').onclick = () => openCreateModal(null);
document.getElementById('activityBtn').onclick = () => toggleActivity();
document.getElementById('activity-close').onclick = () => toggleActivity(false);
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && document.getElementById('modal').classList.contains('open')) { closeModal(); return; }
  if (e.target.matches('input,select,textarea')) return;
  if (e.key === 'r') loadItems();
  if (e.key === 'a') toggleActivity();
});

// --- Modal (detail view / create) ---

async function openDetailModal(itemId) {
  openModal('edit', itemId);
  const hint = document.getElementById('m-hint');
  hint.textContent = 'Loading…';
  try {
    const r = await fetch('/api/boards/' + state.current.id + '/items/' + encodeURIComponent(itemId));
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText);
    const detail = await r.json();
    state.modal.original = detail;
    state.modal.draft = cloneForDraft(detail);
    paintModal(state.modal.draft);
    hint.textContent = '';
  } catch (e) {
    hint.textContent = 'Load failed: ' + e.message;
  }
}

function openCreateModal(columnId) {
  state.modal.targetColumnId = columnId || null;
  const blank = {
    id: null, title: '', description: '',
    labels: [], labelDetails: [], assignees: [], milestone: '', state: 'opened',
  };
  // Pre-apply the target column's scoped label so the new issue lands in that column.
  if (columnId && state.current) {
    const col = state.current.columns.find(c => c.id === columnId);
    if (col && col.kind === 'scoped-label' && col.scopedLabel) {
      blank.labels.push(col.scopedLabel);
      blank.labelDetails.push({ name: col.scopedLabel, color: col.accent });
    } else if (col && col.kind === 'label' && col.mapsToLabel) {
      blank.labels.push(col.mapsToLabel);
      blank.labelDetails.push({ name: col.mapsToLabel, color: col.accent });
    }
  }
  // Carry the board's primary label (the team filter) so created issues show up here.
  if (state.current && state.current.primaryToolLabel && !blank.labels.includes(state.current.primaryToolLabel)) {
    blank.labels.push(state.current.primaryToolLabel);
    blank.labelDetails.push({ name: state.current.primaryToolLabel });
  }
  openModal('create', null);
  state.modal.original = null;
  state.modal.draft = blank;
  paintModal(blank);
}

function openModal(mode, id) {
  state.modal.mode = mode;
  state.modal.id = id;
  const m = document.getElementById('modal');
  m.classList.add('open');
  m.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  document.getElementById('m-save').textContent = mode === 'create' ? 'Create' : 'Save';
  document.getElementById('m-iid').textContent = id ? ('#' + id.split(':').slice(-1)[0]) : 'NEW';
  // Default description tab: Preview when reading an existing issue (focus on
  // rendered content), Write when creating (focus on typing).
  setDescTab(mode === 'create' ? 'write' : 'preview');
  // Draft box only in create mode.
  const draftBox = document.getElementById('m-draft');
  if (draftBox) {
    draftBox.hidden = mode !== 'create';
    if (mode === 'create') {
      document.getElementById('m-rough').value = '';
      document.getElementById('m-draft-hint').textContent = '';
      document.getElementById('m-draft-hint').className = 'hint';
      populateDraftAgents();
    }
  }
  // Pre-focus: rough for create, description for edit.
  setTimeout(() => {
    const el = document.getElementById(mode === 'create' ? 'm-rough' : 'm-desc');
    if (el) el.focus();
  }, 50);
}

/** Toggle between rendered preview and the raw textarea for the description. */
function setDescTab(tab) {
  const preview = document.getElementById('m-desc-preview');
  const textarea = document.getElementById('m-desc');
  if (!preview || !textarea) return;
  const showPreview = tab === 'preview';
  preview.hidden = !showPreview;
  textarea.hidden = showPreview;
  document.querySelectorAll('.desc-tabs .desc-tab').forEach((b) => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });
  if (showPreview) {
    // Re-render with the current draft content so previews stay in sync with
    // in-progress edits (e.g. after an ✨ Draft).
    paintDescriptionPreview(state.modal.draft || {});
  }
}

function paintDescriptionPreview(d) {
  const preview = document.getElementById('m-desc-preview');
  if (!preview) return;
  const text = (state.modal.draft && state.modal.draft.description) || d.description || '';
  if (!text.trim()) {
    preview.classList.add('empty');
    preview.textContent = 'No description yet.';
    return;
  }
  preview.classList.remove('empty');
  // Derive GitLab host+project from the item URL so #123 / !45 / @user become
  // real links. Falls back to plain text rendering when URL isn't available.
  const url = (state.modal.original && state.modal.original.url) || d.url || '';
  const ctx = parseGitLabContext(url);
  preview.innerHTML = renderGitLabMarkdown(text, ctx);
}

/** Extract {host, project} from a GitLab issue/MR URL. Returns null on failure. */
function parseGitLabContext(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // ".../:namespace/:project/-/issues/:iid" or ".../-/merge_requests/..."
    const m = u.pathname.match(/^\\/(.+?)\\/-\\//);
    if (!m) return { host: u.origin, project: '' };
    return { host: u.origin, project: m[1] };
  } catch { return null; }
}

// Alias escapeHtml so the markdown renderer reads concisely.
const esc = escapeHtml;

/* ------------------------------------------------------------------ */
/* Minimal GitLab-flavored Markdown renderer                          */
/* ------------------------------------------------------------------ */
/*  Handles: headings, bold, italic, strikethrough, inline + fenced   */
/*  code, links, images, blockquotes, HR, ordered/unordered lists,    */
/*  task lists (- [ ] / - [x]), tables, paragraphs. Post-process for  */
/*  GitLab refs (#NNN, !NNN, @user).                                  */
/*  Not a full CommonMark implementation — covers the 95% used in     */
/*  issue bodies. Output is assembled from escaped + linkified tokens */
/*  so no user content lands in the DOM unescaped.                    */
/* ------------------------------------------------------------------ */

function renderGitLabMarkdown(md, ctx) {
  const src = String(md || '').replace(/\\r\\n?/g, '\\n');
  // Protect fenced code blocks first — they must pass through untouched.
  // Regex built via RegExp constructor because we're inside a TS template
  // literal and backtick regex literals confuse the bundler.
  const fences = [];
  const fenceRe = new RegExp('\\\\u0060\\\\u0060\\\\u0060(\\\\w*)\\\\n([\\\\s\\\\S]*?)\\\\n\\\\u0060\\\\u0060\\\\u0060', 'g');
  const withoutFences = src.replace(fenceRe, (_m, lang, code) => {
    const idx = fences.length;
    fences.push({ lang, code });
    return '\\u0000FENCE' + idx + '\\u0000';
  });

  const lines = withoutFences.split('\\n');
  let html = '';
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fence placeholder — emit <pre><code>.
    const fm = line.match(/^\\u0000FENCE(\\d+)\\u0000\$/);
    if (fm) {
      const f = fences[+fm[1]];
      const cls = f.lang ? ' class="lang-' + esc(f.lang) + '"' : '';
      html += '<pre><code' + cls + '>' + esc(f.code) + '</code></pre>';
      i++;
      continue;
    }

    // Blank line — paragraph break.
    if (!line.trim()) { i++; continue; }

    // Heading.
    const hm = line.match(/^(#{1,6})\\s+(.*)/);
    if (hm) {
      const level = hm[1].length;
      html += '<h' + level + '>' + inlineMd(hm[2], ctx) + '</h' + level + '>';
      i++;
      continue;
    }

    // Horizontal rule.
    if (/^(---+|\\*\\*\\*+|___+)\$/.test(line.trim())) {
      html += '<hr>';
      i++;
      continue;
    }

    // Blockquote (consecutive > lines).
    if (/^>\\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\\s?/, '')); i++; }
      html += '<blockquote>' + inlineMd(buf.join(' '), ctx) + '</blockquote>';
      continue;
    }

    // Table (GFM) — header row + alignment row + body rows.
    if (line.includes('|') && i + 1 < lines.length && /^\\s*\\|?\\s*(:?-+:?\\s*\\|?)+\\s*\$/.test(lines[i+1])) {
      const headerCells = splitTableRow(line);
      let t = '<table><thead><tr>' + headerCells.map(c => '<th>' + inlineMd(c, ctx) + '</th>').join('') + '</tr></thead><tbody>';
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        const cells = splitTableRow(lines[i]);
        t += '<tr>' + cells.map(c => '<td>' + inlineMd(c, ctx) + '</td>').join('') + '</tr>';
        i++;
      }
      html += t + '</tbody></table>';
      continue;
    }

    // Lists (ordered, unordered, task).
    const ulMatch = line.match(/^(\\s*)[-*+]\\s+(.*)/);
    const olMatch = line.match(/^(\\s*)(\\d+)\\.\\s+(.*)/);
    if (ulMatch || olMatch) {
      const ordered = !!olMatch;
      const tag = ordered ? 'ol' : 'ul';
      let items = '';
      while (i < lines.length) {
        const cur = lines[i];
        const um = cur.match(/^(\\s*)[-*+]\\s+(.*)/);
        const om = cur.match(/^(\\s*)(\\d+)\\.\\s+(.*)/);
        if (!um && !om) break;
        const content = (um ? um[2] : om[3]);
        // Task list item: "- [ ] label" or "- [x] label"
        const tm = content.match(/^\\[( |x|X)\\]\\s+(.*)/);
        if (tm) {
          const checked = tm[1].toLowerCase() === 'x' ? ' checked' : '';
          items += '<li class="task"><input type="checkbox" disabled' + checked + '>' + inlineMd(tm[2], ctx) + '</li>';
        } else {
          items += '<li>' + inlineMd(content, ctx) + '</li>';
        }
        i++;
      }
      html += '<' + tag + '>' + items + '</' + tag + '>';
      continue;
    }

    // Paragraph — absorb consecutive non-empty, non-special lines.
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(\\u0000FENCE|#|>|\\s*[-*+]\\s|\\s*\\d+\\.\\s|---+|===+)/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    html += '<p>' + inlineMd(para.join(' '), ctx) + '</p>';
  }
  return html;
}

function splitTableRow(line) {
  // Strip leading/trailing pipe and split by unescaped pipes.
  const s = line.trim().replace(/^\\||\\|\$/g, '');
  return s.split(/(?<!\\\\)\\|/).map(c => c.trim());
}

function inlineMd(text, ctx) {
  // Inline code — protect first with placeholders. Regex built via RegExp
  // constructor so a TS template literal doesn't choke on backtick escapes.
  const codes = [];
  const inlineCodeRe = new RegExp('\\\\u0060([^\\\\u0060]+)\\\\u0060', 'g');
  let out = text.replace(inlineCodeRe, (_m, c) => {
    const idx = codes.length;
    codes.push(c);
    return '\\u0001CODE' + idx + '\\u0001';
  });
  out = esc(out);
  // Images first so their URLs aren't caught by the link rule.
  out = out.replace(/!\\[([^\\]]*)\\]\\(([^)\\s]+)(?:\\s+\"([^\"]*)\")?\\)/g, (_m, alt, src, title) => {
    return '<img src="' + sanitizeUrl(src) + '" alt="' + esc(alt) + '"' + (title ? ' title="' + esc(title) + '"' : '') + '>';
  });
  // Links [text](url).
  out = out.replace(/\\[([^\\]]+)\\]\\(([^)\\s]+)(?:\\s+\"([^\"]*)\")?\\)/g, (_m, label, href, title) => {
    return '<a href="' + sanitizeUrl(href) + '"' + (title ? ' title="' + esc(title) + '"' : '') + ' target="_blank" rel="noopener">' + label + '</a>';
  });
  // Bold, italic, strikethrough.
  out = out.replace(/\\*\\*([^*\\n]+)\\*\\*/g, '<strong>\$1</strong>');
  out = out.replace(/(?<![\\w*])\\*([^*\\n]+)\\*(?![\\w])/g, '<em>\$1</em>');
  out = out.replace(/(?<![\\w_])_([^_\\n]+)_(?![\\w])/g, '<em>\$1</em>');
  out = out.replace(/~~([^~\\n]+)~~/g, '<del>\$1</del>');
  // Autolink raw URLs.
  out = out.replace(/(^|[\\s(])((?:https?:\\/\\/)[^\\s<)]+)/g, (_m, pre, url) => pre + '<a href="' + sanitizeUrl(url) + '" target="_blank" rel="noopener">' + esc(url) + '</a>');
  // GitLab refs: #NNN (issue), !NNN (MR), @user. Only when we have context.
  if (ctx && ctx.host && ctx.project) {
    out = out.replace(/(^|[\\s(])#(\\d+)\\b/g, (_m, pre, n) => pre + '<a class="ref" href="' + ctx.host + '/' + ctx.project + '/-/issues/' + n + '" target="_blank" rel="noopener">#' + n + '</a>');
    out = out.replace(/(^|[\\s(])!(\\d+)\\b/g, (_m, pre, n) => pre + '<a class="ref" href="' + ctx.host + '/' + ctx.project + '/-/merge_requests/' + n + '" target="_blank" rel="noopener">!' + n + '</a>');
    out = out.replace(/(^|[\\s(])@([A-Za-z0-9_.\\-]+)/g, (_m, pre, u) => pre + '<a class="ref" href="' + ctx.host + '/' + u + '" target="_blank" rel="noopener">@' + u + '</a>');
  }
  // Restore inline code.
  out = out.replace(/\\u0001CODE(\\d+)\\u0001/g, (_m, idx) => '<code>' + esc(codes[+idx]) + '</code>');
  return out;
}

function sanitizeUrl(u) {
  const s = String(u || '').trim();
  // Block javascript: and data: (except safe images) URLs.
  if (/^javascript:/i.test(s)) return '#';
  return esc(s);
}

async function populateDraftAgents() {
  const sel = document.getElementById('m-draft-agent');
  if (!sel) return;
  if (sel.options.length > 1) {
    // Already populated — just sync selection.
    const saved = localStorage.getItem('agentx.draftAgent');
    if (saved) sel.value = saved;
    return;
  }
  try {
    const r = await fetch('/api/agents');
    if (!r.ok) return;
    const list = await r.json();
    sel.innerHTML = '<option value="">Agent…</option>';
    for (const a of list) {
      const o = document.createElement('option');
      o.value = a.id; o.textContent = a.name + ' (' + a.tier + ')';
      sel.appendChild(o);
    }
    const saved = localStorage.getItem('agentx.draftAgent');
    if (saved && list.some(a => a.id === saved)) sel.value = saved;
    sel.onchange = () => localStorage.setItem('agentx.draftAgent', sel.value);
  } catch {}
}

async function draftWithAgent() {
  const rough = document.getElementById('m-rough').value.trim();
  const agentId = document.getElementById('m-draft-agent').value;
  const hint = document.getElementById('m-draft-hint');
  const goBtn = document.getElementById('m-draft-go');
  if (!rough) { hint.textContent = 'Describe the issue first.'; hint.className = 'hint err'; return; }
  if (!agentId) { hint.textContent = 'Pick a drafting agent.'; hint.className = 'hint err'; return; }
  goBtn.disabled = true;
  hint.textContent = 'Drafting with ' + agentId + '…'; hint.className = 'hint';

  // Build board context for convention-aware prompting.
  const board = state.current;
  const col = state.modal.targetColumnId ? board.columns.find(c => c.id === state.modal.targetColumnId) : null;
  const columnLabel = col && (col.scopedLabel || col.mapsToLabel);
  const ctx = {
    boardName: board.name,
    primaryLabel: board.primaryToolLabel,
    project: board.source && board.source.projects && board.source.projects[0],
    knownLabels: state.meta.labels || [],
    members: (state.meta.members || []).map(m => m.username),
    columnLabel: columnLabel,
  };

  try {
    const r = await fetch('/api/draft', {
      method: 'POST', headers: csrfHeaders(),
      body: JSON.stringify({ rough, agent: agentId, context: ctx }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText);
    const drafted = await r.json();
    // Populate the existing form fields.
    document.getElementById('m-title').value = drafted.title || '';
    document.getElementById('m-desc').value = drafted.description || '';
    state.modal.draft.title = drafted.title || '';
    state.modal.draft.description = drafted.description || '';
    state.modal.draft.labels = Array.isArray(drafted.labels) ? drafted.labels.slice() : state.modal.draft.labels;
    if (drafted.assigneeUsername) {
      const m = state.meta.members.find(x => x.username === drafted.assigneeUsername);
      if (!state.modal.draft.assignees.some(a => a.username === drafted.assigneeUsername)) {
        state.modal.draft.assignees.push({
          username: drafted.assigneeUsername, name: m && m.name, avatarUrl: m && m.avatarUrl,
        });
      }
    }
    paintModal(state.modal.draft);
    hint.textContent = 'Draft ready — review and edit before creating.';
    hint.className = 'hint ok';
  } catch (e) {
    hint.textContent = 'Draft failed: ' + e.message;
    hint.className = 'hint err';
  } finally {
    goBtn.disabled = false;
  }
}
function closeModal() {
  const m = document.getElementById('modal');
  m.classList.remove('open');
  m.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  state.modal = { mode: null, id: null, original: null, draft: null };
}
function cloneForDraft(d) {
  return {
    id: d.id, title: d.title || '', description: d.description || '',
    labels: (d.labels || []).slice(), labelDetails: (d.labelDetails || []).slice(),
    assignees: (d.assignees || []).slice(),
    milestone: d.milestone || '',
    state: d.state || 'opened',
    stage: d.stage, url: d.url, createdAt: d.createdAt, updatedAt: d.updatedAt, author: d.author,
  };
}

function paintModal(d) {
  document.getElementById('m-title').value = d.title || '';
  document.getElementById('m-desc').value = d.description || '';
  paintDescriptionPreview(d);

  document.querySelectorAll('#m-state button').forEach(b => {
    b.classList.toggle('active', b.dataset.state === (d.state || 'opened'));
    b.onclick = () => { state.modal.draft.state = b.dataset.state; paintModal(state.modal.draft); };
  });

  renderChipPicker('m-labels', d.labels || [], state.meta.labels, {
    chipStyle: (name) => {
      const match = (d.labelDetails || []).find(l => l.name === name);
      const bg = match && match.color ? match.color : labelColor(name);
      return { background: bg, color: pickTextColor(bg) };
    },
    onAdd: (name) => { if (!state.modal.draft.labels.includes(name)) { state.modal.draft.labels.push(name); paintModal(state.modal.draft); } },
    onRemove: (name) => { state.modal.draft.labels = state.modal.draft.labels.filter(l => l !== name); paintModal(state.modal.draft); },
    placeholder: 'Add label…',
    allowCustom: true,
  });

  const aNames = (d.assignees || []).map(a => a.username);
  renderChipPicker('m-assignees', aNames, state.meta.members.map(m => m.username), {
    chipLabel: (u) => {
      const m = state.meta.members.find(x => x.username === u) || (d.assignees || []).find(x => x.username === u);
      return (m && m.name) || u;
    },
    onAdd: (u) => {
      if (state.modal.draft.assignees.some(a => a.username === u)) return;
      const m = state.meta.members.find(x => x.username === u);
      state.modal.draft.assignees.push({ username: u, name: m && m.name, avatarUrl: m && m.avatarUrl });
      paintModal(state.modal.draft);
    },
    onRemove: (u) => { state.modal.draft.assignees = state.modal.draft.assignees.filter(a => a.username !== u); paintModal(state.modal.draft); },
    placeholder: 'Assign…',
  });

  const ms = document.getElementById('m-milestone');
  ms.innerHTML = '<option value="">None</option>';
  for (const m of state.meta.milestones) {
    const o = document.createElement('option');
    o.value = m.title; o.textContent = m.title + (m.state === 'closed' ? ' (closed)' : '');
    if (m.title === d.milestone) o.selected = true;
    ms.appendChild(o);
  }
  if (d.milestone && !state.meta.milestones.some(m => m.title === d.milestone)) {
    const o = document.createElement('option'); o.value = d.milestone; o.textContent = d.milestone; o.selected = true;
    ms.appendChild(o);
  }
  ms.onchange = () => { state.modal.draft.milestone = ms.value; };

  document.getElementById('m-author').textContent = (d.author && (d.author.name || d.author.username)) || '—';
  document.getElementById('m-created').textContent = d.createdAt ? fmtDate(d.createdAt) : '—';
  document.getElementById('m-updated').textContent = d.updatedAt ? fmtDate(d.updatedAt) : '—';
  const link = document.getElementById('m-link');
  if (d.url) { link.href = d.url; link.style.display = ''; } else { link.style.display = 'none'; }
}

function renderChipPicker(elId, selected, options, opts) {
  const el = document.getElementById(elId);
  el.innerHTML = '';
  for (const name of selected) {
    const chip = document.createElement('span');
    chip.className = 'chip';
    chip.textContent = opts.chipLabel ? opts.chipLabel(name) : name;
    if (opts.chipStyle) {
      const st = opts.chipStyle(name);
      if (st.background) chip.style.background = st.background;
      if (st.color) chip.style.color = st.color;
    }
    const x = document.createElement('span');
    x.className = 'remove'; x.textContent = '✕'; x.title = 'Remove';
    x.onclick = (e) => { e.stopPropagation(); opts.onRemove(name); };
    chip.appendChild(x);
    el.appendChild(chip);
  }
  const dd = document.createElement('span');
  dd.className = 'dropdown';
  const btn = document.createElement('button');
  btn.className = 'add-chip'; btn.textContent = '+ ' + (opts.placeholder || 'Add');
  btn.type = 'button';
  btn.onclick = (e) => {
    e.stopPropagation();
    let menu = dd.querySelector('.menu');
    if (menu) { menu.remove(); return; }
    menu = document.createElement('div');
    menu.className = 'menu';
    const search = document.createElement('input');
    search.className = 'search'; search.type = 'search'; search.placeholder = opts.placeholder || 'Search…';
    menu.appendChild(search);
    const list = document.createElement('div');
    menu.appendChild(list);
    const paintList = (q) => {
      list.innerHTML = '';
      const ql = (q || '').toLowerCase();
      const filtered = options.filter(o => o.toLowerCase().includes(ql) && !selected.includes(o));
      if (filtered.length === 0 && !(opts.allowCustom && q)) {
        const empty = document.createElement('div'); empty.className = 'empty'; empty.textContent = 'No matches';
        list.appendChild(empty);
      }
      for (const o of filtered.slice(0, 50)) {
        const opt = document.createElement('div');
        opt.className = 'opt'; opt.textContent = opts.chipLabel ? opts.chipLabel(o) : o;
        opt.onclick = () => { opts.onAdd(o); menu.remove(); };
        list.appendChild(opt);
      }
      if (opts.allowCustom && q && !options.includes(q) && !selected.includes(q)) {
        const opt = document.createElement('div');
        opt.className = 'opt'; opt.textContent = 'Create "' + q + '"';
        opt.onclick = () => { opts.onAdd(q); menu.remove(); };
        list.appendChild(opt);
      }
    };
    search.oninput = (ev) => paintList(ev.target.value);
    paintList('');
    dd.appendChild(menu);
    setTimeout(() => search.focus(), 0);
    const away = (ev) => {
      if (!dd.contains(ev.target)) { menu.remove(); document.removeEventListener('click', away); }
    };
    setTimeout(() => document.addEventListener('click', away), 0);
  };
  dd.appendChild(btn);
  el.appendChild(dd);
}

async function saveModal() {
  const saveBtn = document.getElementById('m-save');
  const hint = document.getElementById('m-hint');
  saveBtn.disabled = true;
  hint.textContent = 'Saving…';
  const d = state.modal.draft;
  d.title = document.getElementById('m-title').value.trim();
  d.description = document.getElementById('m-desc').value;
  if (!d.title) { hint.textContent = 'Title required'; saveBtn.disabled = false; return; }

  try {
    if (state.modal.mode === 'create') {
      const r = await fetch('/api/boards/' + state.current.id + '/items', {
        method: 'POST', headers: csrfHeaders(),
        body: JSON.stringify({
          title: d.title, description: d.description, labels: d.labels,
          assigneeUsernames: (d.assignees || []).map(a => a.username),
          milestoneTitle: d.milestone || undefined,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText);
      toast('Created ✓', 'ok');
    } else {
      const orig = state.modal.original || {};
      const patch = {};
      if (d.title !== (orig.title || '')) patch.title = d.title;
      if (d.description !== (orig.description || '')) patch.description = d.description;
      const origLabels = (orig.labels || []).slice().sort().join('|');
      const newLabels = (d.labels || []).slice().sort().join('|');
      if (origLabels !== newLabels) patch.labels = d.labels;
      const origA = (orig.assignees || []).map(a => a.username).sort().join('|');
      const newA = (d.assignees || []).map(a => a.username).sort().join('|');
      if (origA !== newA) patch.assigneeUsernames = (d.assignees || []).map(a => a.username);
      if (d.milestone !== (orig.milestone || '')) patch.milestoneTitle = d.milestone || null;

      if (Object.keys(patch).length > 0) {
        const r = await fetch('/api/boards/' + state.current.id + '/items/' + encodeURIComponent(state.modal.id), {
          method: 'PATCH', headers: csrfHeaders(), body: JSON.stringify(patch),
        });
        if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText);
      }
      // State change → move to the appropriate column (reuses transition logic on server).
      if (d.state !== (orig.state || 'opened')) {
        const fromCol = state.current.columns.find(c => c.id === orig.stage);
        const toCol = d.state === 'closed'
          ? state.current.columns.find(c => c.kind === 'closed')
          : state.current.columns.find(c => c.kind === 'open-backlog');
        if (toCol) {
          await fetch('/api/boards/' + state.current.id + '/items/' + encodeURIComponent(state.modal.id) + '/move', {
            method: 'PATCH', headers: csrfHeaders(),
            body: JSON.stringify({ from: fromCol && fromCol.id, to: toCol.id }),
          });
        }
      }
      toast('Saved ✓', 'ok');
    }
    closeModal();
    await loadItems();
    state.meta.labels = collectKnownLabels();
  } catch (e) {
    hint.textContent = 'Save failed: ' + e.message;
  } finally {
    saveBtn.disabled = false;
  }
}

document.getElementById('m-close').onclick = closeModal;
document.getElementById('m-cancel').onclick = closeModal;
document.getElementById('m-save').onclick = saveModal;
document.querySelector('#modal .modal-backdrop').onclick = closeModal;
document.getElementById('m-title').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveModal();
});
const draftGo = document.getElementById('m-draft-go');
if (draftGo) draftGo.onclick = draftWithAgent;
const roughEl = document.getElementById('m-rough');
if (roughEl) roughEl.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) draftWithAgent();
});

// Description Preview/Write tabs. Textarea edits sync to draft so the
// preview re-renders the CURRENT text when the user flips to it.
document.querySelectorAll('.desc-tabs .desc-tab').forEach((b) => {
  b.addEventListener('click', () => setDescTab(b.dataset.tab));
});
const descEl = document.getElementById('m-desc');
if (descEl) descEl.addEventListener('input', () => {
  if (state.modal.draft) state.modal.draft.description = descEl.value;
});

// --- Agent activity panel (live polling of the daemon API) ---

const activity = {
  open: false, timer: null, lastEventAt: 0, eventsRing: [],
};

function toggleActivity(force) {
  activity.open = typeof force === 'boolean' ? force : !activity.open;
  const panel = document.getElementById('activity-panel');
  const btn = document.getElementById('activityBtn');
  panel.classList.toggle('hidden', !activity.open);
  panel.setAttribute('aria-hidden', activity.open ? 'false' : 'true');
  btn.classList.toggle('on', activity.open);
  document.body.classList.toggle('panel-open', activity.open);
  if (activity.open) startActivity(); else stopActivity();
}

async function startActivity() {
  // Same-origin: the dashboard proxies its configured daemon. A direct call
  // to the daemon would be cross-origin, and the daemon no longer allows it.
  document.getElementById('activity-source').textContent = 'dashboard daemon';
  await tickActivity();
  activity.timer = setInterval(tickActivity, 3000);
}
function stopActivity() {
  if (activity.timer) { clearInterval(activity.timer); activity.timer = null; }
}

async function tickActivity() {
  const body = document.getElementById('activity-body');
  try {
    // Fetch /agents (always present) + /business/status (optional — 404 tolerated)
    const [agentsRes, bizRes] = await Promise.all([
      fetch('/api/agents').catch(() => null),
      fetch('/api/business/status').catch(() => null),
    ]);
    if (!agentsRes || !agentsRes.ok) throw new Error('daemon unreachable');
    const agents = await agentsRes.json();
    const biz = bizRes && bizRes.ok ? await bizRes.json() : null;

    // Merge business data (currentItem, onClock, lastReport) per-agent when available.
    const bizByAgent = {};
    if (biz && Array.isArray(biz.employees)) for (const e of biz.employees) bizByAgent[e.agentId || e.id] = e;

    body.innerHTML = '';
    for (const a of agents) {
      const b = bizByAgent[a.id];
      body.appendChild(renderAgentCard(a, b));
    }
    if (agents.length === 0) {
      body.innerHTML = '<div class="empty">No agents on this daemon.</div>';
    }
    // Events ring
    if (biz && biz.recentEvents) appendEvents(biz.recentEvents);
  } catch (e) {
    body.innerHTML = '<div class="empty" style="color:var(--red)">' + escapeHtml(e.message) + '</div>';
  }
}

function renderAgentCard(a, biz) {
  const card = document.createElement('div');
  card.className = 'agent-card';
  const active = (a.active || 0) > 0 || (biz && biz.onClock && biz.busy);
  const dotClass = (a.errors || 0) > 0 ? 'errored' : (active ? 'active' : '');
  const current = biz && biz.currentItem
    ? '<div class="current">Working on <b>' + escapeHtml(biz.currentItem.title || biz.currentItem.id) + '</b></div>'
    : (active ? '<div class="current"><b>' + a.active + ' active task' + (a.active === 1 ? '' : 's') + '</b></div>' : '');
  const modelLabel = a.model ? '<span class="model" title="Model">' + escapeHtml(shortenModelName(a.model)) + '</span>' : '';
  const lastLabel = a.lastActive
    ? '<span class="last" title="' + escapeHtml(new Date(a.lastActive).toLocaleString()) + '">last ' + escapeHtml(fmtAgoBoard(a.lastActive)) + '</span>'
    : '<span class="last muted">never ran</span>';
  card.innerHTML =
    '<div class="top">' +
      '<span class="dot ' + dotClass + '"></span>' +
      '<span class="name">' + escapeHtml(a.name || a.id) + '</span>' +
      '<span class="tier">' + escapeHtml(a.tier || '') + '</span>' +
    '</div>' +
    '<div class="meta">' + modelLabel + lastLabel + '</div>' +
    '<div class="stats">' +
      '<span>Active <b>' + (a.active || 0) + '</b></span>' +
      '<span>Total <b>' + (a.total || 0) + '</b></span>' +
      '<span class="errors">Errors <b>' + (a.errors || 0) + '</b></span>' +
    '</div>' + current;
  return card;
}

function shortenModelName(m) {
  if (!m) return '';
  return String(m)
    .replace(/^claude-/, '')
    .replace(/-\\d{8}$/, '')
    .replace(/\\[1m\\]$/, ' · 1M');
}

function fmtAgoBoard(iso) {
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return s + 's ago';
  const m = Math.floor(s / 60); if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60); if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24); return d + 'd ago';
}

function appendEvents(events) {
  const holder = document.getElementById('activity-events');
  const existingKeys = new Set([...holder.querySelectorAll('.evt')].map(e => e.dataset.key));
  for (const ev of events.slice(-50)) {
    const key = (ev.ts || '') + '|' + (ev.type || '') + '|' + (ev.agentId || '');
    if (existingKeys.has(key)) continue;
    const div = document.createElement('div');
    div.className = 'evt' + (ev.type && ev.type.includes('error') ? ' err' : '');
    div.dataset.key = key;
    div.innerHTML = '<span class="ts">' + (ev.ts ? new Date(ev.ts).toLocaleTimeString() : '') + '</span> ' +
                    '<span class="who">' + escapeHtml(ev.agentId || '—') + '</span> ' +
                    '<span class="what">' + escapeHtml(ev.summary || ev.type || '') + '</span>';
    holder.appendChild(div);
  }
  // Keep last 50 entries
  while (holder.children.length > 50) holder.removeChild(holder.firstChild);
  holder.scrollTop = holder.scrollHeight;
}

loadBoards().catch(e => toast('Init failed: ' + e.message));
`
